import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Square, Bell, Sparkles } from 'lucide-react';
import { unlockAudioContext, playAnnouncementAudio, playAnnouncementSequence, stopAllBrowserAudio } from '../utils/browserAudio';
import websocketService from '../services/websocketService';

export const GlobalLiveAnnouncementSpeaker: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('astravision_browser_audio_enabled');
    return saved === null ? true : saved === 'true';
  });
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [currentLang, setCurrentLang] = useState<string>('en');
  const [audioUnlocked, setAudioUnlocked] = useState<boolean>(false);
  const userInteractedRef = useRef<boolean>(false);

  // Auto-unlock AudioContext on first user interaction anywhere in the window
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!userInteractedRef.current) {
        userInteractedRef.current = true;
        unlockAudioContext();
        setAudioUnlocked(true);
      }
    };

    window.addEventListener('click', handleFirstInteraction, { passive: true });
    window.addEventListener('keydown', handleFirstInteraction, { passive: true });
    window.addEventListener('touchstart', handleFirstInteraction, { passive: true });

    return () => {
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
      window.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, []);

  // Listen to UI start / stop events
  useEffect(() => {
    const handleStarted = (e: any) => {
      const detail = e.detail || {};
      setCurrentlyPlaying(detail.text || 'Playing Announcement');
      if (detail.language) setCurrentLang(detail.language);
    };
    const handleStopped = () => {
      setCurrentlyPlaying(null);
    };

    window.addEventListener('astravision_audio_started', handleStarted);
    window.addEventListener('astravision_audio_stopped', handleStopped);

    return () => {
      window.removeEventListener('astravision_audio_started', handleStarted);
      window.removeEventListener('astravision_audio_stopped', handleStopped);
    };
  }, []);

  // Listen to WebSocket broadcasts
  useEffect(() => {
    websocketService.connect();

    const handleAnnouncement = (data: any) => {
      if (!enabled) return;
      unlockAudioContext();

      // Check for multi-language sequence
      if (data.sequence && Array.isArray(data.sequence) && data.sequence.length > 0) {
        const delayMs = typeof data.delay_sec === 'number' ? data.delay_sec * 1000 : 1500;
        playAnnouncementSequence({
          items: data.sequence.map((s: any) => ({
            audioUrl: s.audio_url,
            text: s.text || data.message,
            language: s.language || 'en'
          })),
          delayMs,
          volume: 1.0,
          onStart: () => setCurrentlyPlaying(data.message),
          onItemChange: (_idx, item) => {
            setCurrentlyPlaying(item.text);
            setCurrentLang(item.language || 'en');
          },
          onEnd: () => setCurrentlyPlaying(null)
        });
      } else {
        // Single language broadcast
        playAnnouncementAudio({
          audioUrl: data.audio_url,
          text: data.message || 'AstraVision Safety Announcement',
          language: data.language || 'en',
          volume: 1.0,
          onStart: () => {
            setCurrentlyPlaying(data.message);
            setCurrentLang(data.language || 'en');
          },
          onEnd: () => setCurrentlyPlaying(null),
          onError: () => setCurrentlyPlaying(null)
        });
      }
    };

    const unsubscribe = websocketService.subscribe('announcement_started', handleAnnouncement);
    const handleCustomWs = (e: any) => handleAnnouncement(e.detail);
    window.addEventListener('astravision_ws_announcement', handleCustomWs);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      window.removeEventListener('astravision_ws_announcement', handleCustomWs);
    };
  }, [enabled]);

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem('astravision_browser_audio_enabled', String(next));
    if (!next) {
      stopAllBrowserAudio();
      setCurrentlyPlaying(null);
    } else {
      unlockAudioContext();
    }
  };

  const handleStop = (e: React.MouseEvent) => {
    e.stopPropagation();
    stopAllBrowserAudio();
    setCurrentlyPlaying(null);
  };

  return (
    <div className="flex items-center gap-1.5">
      {currentlyPlaying ? (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-lg animate-pulse text-xs font-medium">
          <Volume2 className="w-4 h-4 text-emerald-400 animate-bounce" />
          <span className="hidden sm:inline font-mono uppercase text-[10px] px-1 py-0.2 rounded bg-black/30">
            {currentLang.toUpperCase()}
          </span>
          <span className="max-w-[140px] md:max-w-[260px] truncate">
            {currentlyPlaying}
          </span>
          <button
            type="button"
            onClick={handleStop}
            className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            title="Stop announcement audio"
          >
            <Square className="w-3 h-3 fill-current" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggleEnabled}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
            enabled
              ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/20'
              : 'bg-gray-500/10 text-gray-400 border-gray-500/30 hover:bg-gray-500/20'
          }`}
          title={enabled ? "AI Browser Announcements: Active (Click to mute)" : "AI Browser Announcements: Muted (Click to enable)"}
        >
          {enabled ? (
            <>
              <Volume2 className="w-3.5 h-3.5 text-indigo-400" />
              <span className="hidden lg:inline text-[11px]">Auto Voice</span>
            </>
          ) : (
            <>
              <VolumeX className="w-3.5 h-3.5 text-gray-400" />
              <span className="hidden lg:inline text-[11px]">Muted</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default GlobalLiveAnnouncementSpeaker;

